import { useForm } from '@shared/composables/useForm';
import { useRoute } from '@shared/composables/useRoute';
import { usePayload } from '@shared/composables/usePayload';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import { type ITransactionType, TransactionType } from '@app/purchase/transaction-type/models/TransactionType';
import type { FormOptions } from '@shared/types/Form';
import { useFetchListData } from '@shared/composables/useFetchListData.ts';

export function useTransactionTypeRepository<T extends ITransactionType = ITransactionType>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                TransactionType.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) =>
                    usePayload<T>(data, {
                        exclude: TransactionType.create(data).hidden(),
                    }),
                ).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(TransactionType.routes().update, {
                        id,
                    }),
                    method: TransactionType.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(TransactionType.routes().destroy, {
                        id,
                    }),
                    method: TransactionType.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        return {
            store,
            update,
            destroy,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(TransactionType.routes().show, { id }) };
        };

        const list = () => {
            const { data: transactionTypes, processing: fetchingTransactionTypes } = useFetchListData<TransactionType>(useRoute().build(TransactionType.routes().index), 'appSection@owner::IndexTransactionTypesPage', (payload) => TransactionType.from(payload));
            return { transactionTypes, fetchingTransactionTypes };
        };

        return {
            show,
            list,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
